import { parseURI, playlistV2URI, URITypeMap } from '@spotify-internal/uri';

const prepareTextUri = text =>
  text
    .trim()
    .replace(/^Spotify/, 'spotify')
    .split('?')
    .shift();

// , ? : @ & = + $ # are not correctly encoded now
// to avoid failed claims and requests we encode separately for user URIs
// + is encoded to space now by the SpotifyURI library and we want to avoid that
const handleSpecialChars = text => {
  if (!text.startsWith('spotify:user:')) {
    return text;
  }

  const userIdRule = /spotify:user:(.*)/;
  const userId = userIdRule.exec(text);

  if (userId === null) {
    return text;
  }

  // Check for playlist URI part
  const playlistIndex = userId[1].indexOf(':playlist:');
  if (playlistIndex === -1) {
    // No playlist part
    const userIdComponent = encodeURIComponent(userId[1]);
    return `spotify:user:${userIdComponent}`;
  }

  const userIdComponent = encodeURIComponent(userId[1].substr(0, playlistIndex));
  const playlistIdComponent = userId[1].substr(playlistIndex);
  return `spotify:user:${userIdComponent}${playlistIdComponent}`;
};

export const isValidSpotifyUri = (text, acceptedUriTypes) => {
  if (!text) {
    return false;
  }

  if (!acceptedUriTypes || acceptedUriTypes.length < 1) {
    return false;
  }

  const uri = parseURI(prepareTextUri(text));

  return !!uri && acceptedUriTypes.includes(uri.type);
};

export const isSpotifyUri = text => {
  if (!text) {
    return false;
  }

  return !!parseURI(prepareTextUri(text));
};

export const getValidSpotifyUri = value => {
  const text = prepareTextUri(value);
  const encodedSpecialsCharUri = handleSpecialChars(text);
  const uriComponents = parseURI(encodedSpecialsCharUri);
  return uriComponents.toURI();
};

export const convertPlaylistV1ToV2 = uri => {
  // Converting Playlist V1 URI to Playlist V2, as it's not supported
  // later on when we check for content type existence on the platform
  const uriObject = parseURI(uri);
  return uriObject?.type === URITypeMap.PLAYLIST ? playlistV2URI(uriObject.id).toURI() : uri;
};

export const getUriType = text => {
  if (!text) {
    return null;
  }
  const rawText = prepareTextUri(text);
  const uri = parseURI(handleSpecialChars(rawText));
  if (!uri) {
    return null;
  }
  return uri.type;
};

export const splitTextByDelim = text => (text ?? '').trim().split(/\n|\s|,|;/g);
export const getValidSpotifyUriList = list => list.map(getValidSpotifyUri);

export const isValidSpotifyUriList = (text, acceptedUriTypes, maximumNumberOfURIs = 200) => {
  if (!acceptedUriTypes || acceptedUriTypes.length < 1) {
    return false;
  }

  const possibleSpotifyUris = splitTextByDelim(text);

  if (possibleSpotifyUris.length > maximumNumberOfURIs) {
    return false;
  }

  const isNotValidUri = t => !isValidSpotifyUri(t, acceptedUriTypes);
  const isNotEmpty = t => t.trim() !== '';

  const invalidValues = possibleSpotifyUris.filter(isNotEmpty).filter(isNotValidUri);

  return invalidValues.length === 0;
};
